import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { useAuthStore, type UserRole } from '@/stores/auth'
import { SCHOOL_SECTION_ROLES, canOpenPath, landingRouteName, type Principal } from './access'

/** Report tabs in the order the Reports page shows them. */
const REPORT_TABS = ['finance', 'operations', 'canteen', 'transactions', 'saved'] as const

/**
 * Route structure (UI Spec §5.1, §5.2):
 *  - /auth/*           shared authentication shell (AuthLayout)
 *  - /platform/*       Super Admin Console (PlatformLayout)
 *  - /school/*         School Admin Portal (SchoolLayout)
 *
 * Route meta drives the navigation guard:
 *  - `requiredRoles` — a user must have at least one of these roles.
 *  - `requiredPermissions` — the user must also have each of these permission
 *    strings in their `permissions` set (RBAC matrix per BRD §10). The auth
 *    store will expose this set once the permissions claim lands; until then
 *    the check is a no-op for Super Admins who have all permissions.
 *  - `public` — anonymous access (login, reset, MFA).
 */

declare module 'vue-router' {
  interface RouteMeta {
    requiredRoles?: UserRole[]
    requiredPermissions?: string[]
    // UI Spec §6.4 — displayed in breadcrumbs and the browser title.
    title?: string
    // Allow anonymous access (login, password reset, etc.).
    public?: boolean
    // Per-page width token. `wide` (dashboards, list pages) drops the
    // max-width cap so charts/tables can use the full viewport; `narrow`
    // (the default — forms, settings, detail pages) keeps the comfortable
    // 1280px reading column.
    width?: 'wide' | 'narrow'
  }
}

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'landing',
    component: () => import('@/pages/marketing/Landing.vue'),
    meta: { title: 'SYNC — School management & canteen wallets', public: true },
  },

  // --- Shared auth shell -----------------------------------------------------
  {
    path: '/auth',
    component: () => import('@/layouts/AuthLayout.vue'),
    children: [
      {
        path: 'login',
        name: 'auth.login',
        component: () => import('@/pages/auth/Login.vue'),
        meta: { title: 'Sign in', public: true },
      },
      {
        path: 'mfa',
        name: 'auth.mfa',
        component: () => import('@/pages/auth/MfaChallenge.vue'),
        meta: { title: 'Verify sign-in', public: true },
      },
      {
        path: 'activate',
        name: 'auth.activate',
        component: () => import('@/pages/auth/Activate.vue'),
        meta: { title: 'Activate account', public: true },
      },
      {
        // The API's reset email links to /auth/reset-password/<token> (token
        // in the path); older links used ?token=. Both must land here: an
        // unmatched path falls through to the catch-all and silently becomes
        // the login page.
        path: 'reset-password/:token?',
        name: 'auth.reset-password',
        component: () => import('@/pages/auth/ResetPassword.vue'),
        meta: { title: 'Reset password', public: true },
      },
    ],
  },

  // Legacy email-link path — early activation emails pointed at /activate
  // (no /auth prefix). Redirect into the AuthLayout-mounted route while
  // preserving the token query param.
  {
    path: '/activate',
    redirect: (to) => ({ name: 'auth.activate', query: to.query }),
  },

  // --- Super Admin Console ---------------------------------------------------
  {
    path: '/platform',
    component: () => import('@/layouts/PlatformLayout.vue'),
    meta: { requiredRoles: ['platform_super_admin', 'super_admin'] },
    children: [
      {
        path: '',
        redirect: { name: 'platform.dashboard' },
      },
      {
        path: 'dashboard',
        name: 'platform.dashboard',
        component: () => import('@/pages/platform/Dashboard.vue'),
        meta: {
          title: 'Platform Dashboard',
          requiredRoles: ['platform_super_admin', 'super_admin'],
          requiredPermissions: ['platform:read'],
          width: 'wide',
        },
      },
      {
        path: 'tenants',
        name: 'platform.tenants',
        component: () => import('@/pages/platform/Tenants.vue'),
        meta: {
          title: 'Tenants',
          requiredRoles: ['platform_super_admin', 'super_admin'],
          requiredPermissions: ['tenant:read'],
          width: 'wide',
        },
      },
      {
        path: 'tenants/new',
        name: 'platform.tenant-new',
        component: () => import('@/pages/platform/TenantOnboardWizard.vue'),
        meta: {
          title: 'Onboard tenant',
          requiredRoles: ['platform_super_admin', 'super_admin'],
          requiredPermissions: ['tenant:create'],
        },
      },
      {
        path: 'tenants/:tenantId',
        name: 'platform.tenant-detail',
        component: () => import('@/pages/platform/TenantDetail.vue'),
        meta: {
          title: 'Tenant Detail',
          requiredRoles: ['platform_super_admin', 'super_admin'],
          requiredPermissions: ['tenant:read'],
        },
        props: true,
      },
      {
        path: 'students-biometrics',
        name: 'platform.students-biometrics',
        component: () => import('@/pages/platform/StudentsBiometrics.vue'),
        meta: {
          title: 'Students Biometrics',
          requiredRoles: ['platform_super_admin', 'super_admin'],
          width: 'wide',
        },
      },
      {
        path: 'wallet-reconciliation',
        name: 'platform.wallet-reconciliation',
        component: () => import('@/pages/platform/WalletReconciliation.vue'),
        meta: {
          title: 'Wallet Reconciliation',
          requiredRoles: ['platform_super_admin', 'super_admin'],
          width: 'wide',
        },
      },
      {
        path: 'audit',
        name: 'platform.audit',
        component: () => import('@/pages/platform/AuditLog.vue'),
        meta: {
          title: 'Audit Log',
          requiredRoles: ['platform_super_admin', 'super_admin'],
          requiredPermissions: ['audit:read'],
          width: 'wide',
        },
      },
      {
        path: 'pos-terminals',
        name: 'platform.pos-terminals',
        component: () => import('@/pages/platform/PosTerminalsPage.vue'),
        meta: {
          title: 'POS terminals',
          requiredRoles: ['platform_super_admin', 'super_admin'],
          width: 'wide',
        },
      },
      {
        path: 'health',
        name: 'platform.health',
        component: () => import('@/pages/platform/SystemHealth.vue'),
        meta: {
          title: 'System Health',
          requiredRoles: ['platform_super_admin', 'super_admin'],
          requiredPermissions: ['platform:read'],
          width: 'wide',
        },
      },
      {
        path: 'plan-modules',
        name: 'platform.planModules',
        component: () => import('@/pages/platform/PlanModules.vue'),
        meta: {
          title: 'Plan Modules',
          requiredRoles: ['platform_super_admin', 'super_admin'],
          width: 'wide',
        },
      },
      {
        path: 'curriculum-templates',
        name: 'platform.curriculumTemplates',
        component: () => import('@/pages/platform/CurriculumTemplates.vue'),
        meta: {
          title: 'Curriculum Templates',
          requiredRoles: ['platform_super_admin', 'super_admin'],
          width: 'wide',
        },
      },
      {
        path: 'transport-tags',
        name: 'platform.transportTags',
        component: () => import('@/pages/platform/TransportTags.vue'),
        meta: {
          title: 'Transport Tags',
          requiredRoles: ['platform_super_admin', 'super_admin'],
          width: 'wide',
        },
      },
      // Phase 6 — Super Admin Portal improvements: 6 new routes. Pages are
      // placeholders here; Phase 7 ships the real implementations.
      {
        path: 'users',
        name: 'platform.users',
        component: () => import('@/pages/platform/PlatformUsers.vue'),
        meta: {
          title: 'Platform Users',
          requiredRoles: ['platform_super_admin', 'super_admin'],
          width: 'wide',
        },
      },
      {
        path: 'plans',
        name: 'platform.subscriptions',
        component: () => import('@/pages/platform/Subscriptions.vue'),
        meta: {
          title: 'Subscriptions',
          requiredRoles: ['platform_super_admin', 'super_admin'],
          width: 'wide',
        },
      },
      {
        path: 'dashboard/parents-by-school',
        name: 'platform.dashboard.parents',
        component: () => import('@/pages/platform/dashboard/ParentsBySchool.vue'),
        meta: {
          title: 'Total Parents per School',
          requiredRoles: ['platform_super_admin', 'super_admin'],
          width: 'wide',
        },
      },
      {
        path: 'dashboard/students-by-school',
        name: 'platform.dashboard.students',
        component: () => import('@/pages/platform/dashboard/StudentsBySchool.vue'),
        meta: {
          title: 'Total Students per School',
          requiredRoles: ['platform_super_admin', 'super_admin'],
          width: 'wide',
        },
      },
      {
        // Per-tenant student list (drill-down from StudentsBySchool count click).
        // Backed by /v1/platform/tenants/{tenantId}/students (V232 SECURITY DEFINER).
        path: 'dashboard/tenants/:tenantId/students',
        name: 'platform.dashboard.tenant-students',
        component: () => import('@/pages/platform/dashboard/TenantStudents.vue'),
        meta: {
          title: 'Students',
          requiredRoles: ['platform_super_admin', 'super_admin'],
          width: 'wide',
        },
      },
      {
        // Per-tenant parent list (drill-down from ParentsBySchool count click).
        path: 'dashboard/tenants/:tenantId/parents',
        name: 'platform.dashboard.tenant-parents',
        component: () => import('@/pages/platform/dashboard/TenantParents.vue'),
        meta: {
          title: 'Parents',
          requiredRoles: ['platform_super_admin', 'super_admin'],
          width: 'wide',
        },
      },
      {
        path: 'dashboard/top-schools',
        name: 'platform.dashboard.top',
        component: () => import('@/pages/platform/dashboard/TopSchools.vue'),
        meta: {
          title: 'Top Performing Schools',
          requiredRoles: ['platform_super_admin', 'super_admin'],
          width: 'wide',
        },
      },
      {
        path: 'dashboard/platform-payments',
        name: 'platform.dashboard.payments',
        component: () => import('@/pages/platform/dashboard/PlatformPayments.vue'),
        meta: {
          title: 'Platform Payments',
          requiredRoles: ['platform_super_admin', 'super_admin'],
          width: 'wide',
        },
      },
      // ADR-0017 — Platform Billing super-admin section.
      {
        path: 'billing',
        redirect: '/platform/billing/dashboard',
      },
      {
        path: 'billing/dashboard',
        name: 'platform.billing.dashboard',
        component: () => import('@/pages/platform/billing/BillingDashboard.vue'),
        meta: {
          title: 'Platform Billing — Dashboard',
          requiredRoles: ['platform_super_admin', 'super_admin', 'platform_billing_officer'],
          width: 'wide',
        },
      },
      {
        path: 'billing/periods',
        name: 'platform.billing.periods',
        component: () => import('@/pages/platform/billing/BillingPeriods.vue'),
        meta: {
          title: 'Platform Billing — Periods',
          requiredRoles: ['platform_super_admin', 'super_admin', 'platform_billing_officer'],
          width: 'wide',
        },
      },
      {
        path: 'billing/schools',
        name: 'platform.billing.schools',
        component: () => import('@/pages/platform/billing/BillingSchools.vue'),
        meta: {
          title: 'Platform Billing — Schools',
          requiredRoles: ['platform_super_admin', 'super_admin', 'platform_billing_officer'],
          width: 'wide',
        },
      },
      {
        path: 'billing/invoices',
        name: 'platform.billing.invoices',
        component: () => import('@/pages/platform/billing/BillingInvoices.vue'),
        meta: {
          title: 'Platform Billing — Invoices',
          requiredRoles: ['platform_super_admin', 'super_admin', 'platform_billing_officer'],
          width: 'wide',
        },
      },
      {
        path: 'billing/settings',
        name: 'platform.billing.settings',
        component: () => import('@/pages/platform/billing/BillingSettings.vue'),
        meta: {
          title: 'Platform Billing — Settings',
          requiredRoles: ['platform_super_admin', 'super_admin'],
          width: 'wide',
        },
      },
    ],
  },

  // --- School Admin Portal ---------------------------------------------------
  {
    path: '/school',
    component: () => import('@/layouts/SchoolLayout.vue'),
    // A section gate, not an authorisation decision: every child carries the
    // roles that may open it (CANTEEN_ADMIN to the canteen pages, ADR-0016;
    // teachers and heads of department to exams and attendance), and the server
    // refuses whatever the token does not cover. Naming only the two admin
    // roles here made every other role's children unreachable — the parent is
    // matched first — so an HOD signed in successfully and bounced straight
    // back to the login screen. See router/access.ts.
    meta: { requiredRoles: SCHOOL_SECTION_ROLES },
    children: [
      {
        path: '',
        redirect: { name: 'school.dashboard' },
      },
      {
        path: 'dashboard',
        name: 'school.dashboard',
        component: () => import('@/pages/school/Dashboard.vue'),
        meta: { title: 'Dashboard', requiredRoles: ['school_admin'], width: 'wide' },
      },
      {
        path: 'people',
        name: 'school.people',
        component: () => import('@/pages/school/People.vue'),
        meta: { title: 'People', requiredRoles: ['school_admin'], width: 'wide' },
      },
      {
        path: 'people/parents',
        name: 'school.parents',
        component: () => import('@/pages/school/people/ParentsList.vue'),
        meta: { title: 'Parents', requiredRoles: ['school_admin'], width: 'wide' },
      },
      {
        path: 'people/parents/bulk-upload',
        name: 'school.parents-bulk-upload',
        component: () => import('@/pages/school/people/ParentBulkUploadWizard.vue'),
        meta: { title: 'Bulk upload parents', requiredRoles: ['school_admin'] },
      },
      {
        path: 'people/parents/:parentId',
        name: 'school.parent-detail',
        component: () => import('@/pages/school/people/ParentDetail.vue'),
        meta: { title: 'Parent detail', requiredRoles: ['school_admin'] },
        props: true,
      },
      {
        path: 'people/students',
        name: 'school.students',
        component: () => import('@/pages/school/people/StudentsList.vue'),
        meta: { title: 'Students', requiredRoles: ['school_admin'], width: 'wide' },
      },
      {
        path: 'people/students/promotion',
        name: 'school.cohort-promotion',
        component: () => import('@/pages/school/people/CohortPromotionPage.vue'),
        meta: { title: 'Promote students', requiredRoles: ['school_admin'], width: 'wide' },
      },
      {
        path: 'people/students/bulk-upload',
        name: 'school.students-bulk-upload',
        component: () => import('@/pages/school/people/StudentBulkUploadWizard.vue'),
        meta: { title: 'Bulk upload students', requiredRoles: ['school_admin'] },
      },
      {
        path: 'people/students/:studentId',
        name: 'school.student-detail',
        component: () => import('@/pages/school/people/StudentDetail.vue'),
        meta: { title: 'Student detail', requiredRoles: ['school_admin'] },
        props: true,
      },
      {
        path: 'people/staff',
        name: 'school.staff',
        component: () => import('@/pages/school/people/StaffsList.vue'),
        meta: { title: 'Staff', requiredRoles: ['school_admin'], width: 'wide' },
      },
      {
        path: 'people/staff/:staffId',
        name: 'school.staff-detail',
        component: () => import('@/pages/school/people/StaffDetail.vue'),
        // Matches the staff LIST route's roles. Widening this alone would be
        // pointless — a principal or HOD cannot reach the list to click through.
        meta: { title: 'Staff detail', requiredRoles: ['school_admin'], width: 'wide' },
        props: true,
      },
      {
        path: 'finance',
        name: 'school.finance',
        component: () => import('@/pages/school/Finance.vue'),
        meta: { anyOfModules: ['FINANCE'], requiredCapabilities: ['finance.billing'], title: 'Finance', requiredRoles: ['school_admin'], width: 'wide' },
      },
      {
        path: 'finance/fee-items',
        name: 'school.finance.fee-items',
        component: () => import('@/pages/school/finance/FeeItemsPage.vue'),
        meta: { anyOfModules: ['FINANCE'], requiredCapabilities: ['finance.billing'], title: 'Fee Items', requiredRoles: ['school_admin'], width: 'wide' },
      },
      {
        path: 'finance/fee-item-categories',
        name: 'school.finance.fee-item-categories',
        component: () => import('@/pages/school/finance/FeeItemCategoriesPage.vue'),
        meta: { anyOfModules: ['FINANCE'], requiredCapabilities: ['finance.billing'], title: 'Fee Categories', requiredRoles: ['school_admin'], width: 'wide' },
      },
      {
        path: 'finance/fee-structures',
        name: 'school.finance.fee-structures',
        component: () => import('@/pages/school/finance/FeeStructuresPage.vue'),
        meta: { anyOfModules: ['FINANCE'], requiredCapabilities: ['finance.structures'], title: 'Fee Structures', requiredRoles: ['school_admin'], width: 'wide' },
      },
      // TODO: fee-structures/:id route — FeeStructureDetail.vue not yet shipped.
      // Re-enable this route once the page lands; the router previously
      // referenced a non-existent module, which blocked Vite pre-transform.
      {
        path: 'finance/invoice-batches',
        name: 'school.finance.invoice-batches',
        component: () => import('@/pages/school/finance/InvoiceBatchesPage.vue'),
        meta: { anyOfModules: ['FINANCE'], requiredCapabilities: ['finance.billing'], title: 'Invoice batches', requiredRoles: ['school_admin'], width: 'wide' },
      },
      {
        path: 'finance/invoices',
        name: 'school.finance.invoices',
        component: () => import('@/pages/school/finance/InvoicesPage.vue'),
        meta: { anyOfModules: ['FINANCE'], requiredCapabilities: ['finance.billing'], title: 'Invoices', requiredRoles: ['school_admin'], width: 'wide' },
      },
      {
        path: 'finance/invoices/:id/view',
        name: 'school.finance.invoice-view',
        component: () => import('@/pages/school/finance/InvoiceViewPage.vue'),
        meta: { anyOfModules: ['FINANCE'], requiredCapabilities: ['finance.billing'], title: 'View invoice', requiredRoles: ['school_admin'] },
      },
      {
        path: 'finance/receipts',
        name: 'school.finance.receipts',
        component: () => import('@/pages/school/finance/ReceiptsPage.vue'),
        meta: { anyOfModules: ['FINANCE'], requiredCapabilities: ['finance.billing'], title: 'Receipts', requiredRoles: ['school_admin'], width: 'wide' },
      },
      {
        path: 'finance/credit-notes',
        name: 'school.finance.credit-notes',
        component: () => import('@/pages/school/finance/CreditNotesPage.vue'),
        meta: { anyOfModules: ['FINANCE'], requiredCapabilities: ['finance.creditnotes'], title: 'Credit notes', requiredRoles: ['school_admin'], width: 'wide' },
      },
      {
        path: 'finance/arrears',
        name: 'school.finance.arrears',
        component: () => import('@/pages/school/finance/ArrearsPage.vue'),
        meta: { anyOfModules: ['FINANCE'], requiredCapabilities: ['finance.arrears'], title: 'Arrears', requiredRoles: ['school_admin'], width: 'wide' },
      },
      {
        path: 'finance/statements',
        name: 'school.finance.statements',
        component: () => import('@/pages/school/finance/StatementsPage.vue'),
        meta: { anyOfModules: ['FINANCE'], requiredCapabilities: ['finance.statements'], title: 'Statements', requiredRoles: ['school_admin'], width: 'wide' },
      },
      {
        path: 'canteen',
        name: 'school.canteen',
        component: () => import('@/pages/school/Canteen.vue'),
        meta: { requiredCapabilities: ['canteen'], title: 'Canteen', requiredRoles: ['school_admin', 'canteen_admin'], width: 'wide' },
      },
      {
        path: 'canteen/dashboard',
        name: 'school.canteen.dashboard',
        component: () => import('@/pages/school/canteen/CanteenDashboardPage.vue'),
        meta: { requiredCapabilities: ['canteen'], title: 'Canteen — Dashboard', requiredRoles: ['school_admin', 'canteen_admin'], width: 'wide' },
      },
      {
        path: 'canteen/outlets',
        name: 'school.canteen.outlets',
        component: () => import('@/pages/school/canteen/OutletsPage.vue'),
        meta: { requiredCapabilities: ['canteen'], title: 'Outlets', requiredRoles: ['school_admin', 'canteen_admin'], width: 'wide' },
      },
      {
        path: 'canteen/users',
        name: 'school.canteen.users',
        component: () => import('@/pages/school/canteen/CanteenUsersPage.vue'),
        meta: { requiredCapabilities: ['canteen'], title: 'Canteen users', requiredRoles: ['school_admin', 'canteen_admin'], width: 'wide' },
      },
      {
        path: 'canteen/pos-terminals',
        name: 'school.canteen.pos-terminals',
        component: () => import('@/pages/school/canteen/PosTerminalsPage.vue'),
        meta: { requiredCapabilities: ['canteen'], title: 'POS terminals', requiredRoles: ['school_admin', 'canteen_admin'], width: 'wide' },
      },
      {
        path: 'canteen/pos',
        name: 'school.canteen.pos',
        component: () => import('@/pages/school/canteen/PosPage.vue'),
        meta: { requiredCapabilities: ['canteen'],
          title: 'POS',
          requiredRoles: ['school_admin', 'canteen_admin', 'canteen_manager', 'canteen_cashier'],
        },
      },
      {
        path: 'canteen/menu',
        name: 'school.canteen.menu',
        component: () => import('@/pages/school/canteen/MenuPage.vue'),
        meta: { requiredCapabilities: ['canteen'], title: 'Menu', requiredRoles: ['school_admin', 'canteen_admin'], width: 'wide' },
      },
      {
        path: 'canteen/suppliers',
        name: 'school.canteen.suppliers',
        component: () => import('@/pages/school/canteen/SuppliersPage.vue'),
        meta: { requiredCapabilities: ['canteen'], title: 'Suppliers', requiredRoles: ['school_admin', 'canteen_admin'], width: 'wide' },
      },
      {
        path: 'canteen/sales',
        name: 'school.canteen.sales',
        component: () => import('@/pages/school/canteen/SalesPage.vue'),
        meta: { requiredCapabilities: ['canteen'], title: 'Sales', requiredRoles: ['school_admin', 'canteen_admin'], width: 'wide' },
      },
      {
        path: 'canteen/open-orders',
        name: 'school.canteen.open-orders',
        component: () => import('@/pages/school/canteen/OpenOrdersPage.vue'),
        meta: { requiredCapabilities: ['canteen'],
          title: 'Open orders',
          requiredRoles: ['school_admin', 'canteen_admin', 'canteen_manager', 'canteen_cashier'],
          width: 'wide',
        },
      },
      {
        path: 'canteen/stock-balances',
        name: 'school.canteen.stock-balances',
        component: () => import('@/pages/school/canteen/StockBalancesPage.vue'),
        meta: { requiredCapabilities: ['canteen'], title: 'Stock balances', requiredRoles: ['school_admin', 'canteen_admin'], width: 'wide' },
      },
      {
        path: 'canteen/purchase-orders',
        name: 'school.canteen.purchase-orders',
        component: () => import('@/pages/school/canteen/PurchaseOrdersPage.vue'),
        meta: { requiredCapabilities: ['canteen'], title: 'Purchase orders', requiredRoles: ['school_admin', 'canteen_admin'], width: 'wide' },
      },
      {
        path: 'canteen/goods-receipts',
        name: 'school.canteen.goods-receipts',
        component: () => import('@/pages/school/canteen/GoodsReceiptsPage.vue'),
        meta: { requiredCapabilities: ['canteen'], title: 'Goods receipts', requiredRoles: ['school_admin', 'canteen_admin'], width: 'wide' },
      },
      {
        path: 'canteen/reorder-alerts',
        name: 'school.canteen.reorder-alerts',
        component: () => import('@/pages/school/canteen/ReorderAlertsPage.vue'),
        meta: { requiredCapabilities: ['canteen'], title: 'Reorder alerts', requiredRoles: ['school_admin', 'canteen_admin'], width: 'wide' },
      },
      {
        path: 'canteen/valuation',
        name: 'school.canteen.valuation',
        component: () => import('@/pages/school/canteen/InventoryValuationPage.vue'),
        meta: { requiredCapabilities: ['canteen'], title: 'Inventory valuation', requiredRoles: ['school_admin', 'canteen_admin'], width: 'wide' },
      },
      {
        path: 'canteen/wastage',
        name: 'school.canteen.wastage',
        component: () => import('@/pages/school/canteen/WastagePage.vue'),
        meta: { requiredCapabilities: ['canteen'], title: 'Wastage', requiredRoles: ['school_admin', 'canteen_admin'], width: 'wide' },
      },
      {
        path: 'canteen/stock-transfers',
        name: 'school.canteen.stock-transfers',
        component: () => import('@/pages/school/canteen/StockTransfersPage.vue'),
        meta: { requiredCapabilities: ['canteen'], title: 'Stock transfers', requiredRoles: ['school_admin', 'canteen_admin'], width: 'wide' },
      },
      {
        path: 'canteen/stocktakes',
        name: 'school.canteen.stocktakes',
        component: () => import('@/pages/school/canteen/StocktakesPage.vue'),
        meta: { requiredCapabilities: ['canteen'], title: 'Stocktakes', requiredRoles: ['school_admin', 'canteen_admin'], width: 'wide' },
      },
      {
        path: 'exams',
        component: () => import('@/pages/school/Exams.vue'),
        // teacher and homeroom_teacher are included because authoring a paper
        // for a subject you teach is the point. The service scopes authoring to
        // the caller's teacher_allocations; the route only decides who sees the
        // section at all.
        // The whole section follows the module, so a child added later without
        // its own capability cannot keep the menu alive — Report cards did.
        meta: {
          requiredCapabilities: ['exams'],
          title: 'Exams',
          requiredRoles: ['school_admin', 'principal', 'deputy_principal',
                          'head_of_department', 'teacher', 'homeroom_teacher'],
          width: 'wide',
        },
        children: [
          { path: '', redirect: { name: 'school.exams.list' } },
          {
            path: 'list', name: 'school.exams.list',
            component: () => import('@/pages/school/exams/ExamsListPage.vue'),
            meta: { requiredCapabilities: ['exams'],
              title: 'Exams',
              requiredRoles: ['school_admin', 'principal', 'deputy_principal',
                              'head_of_department', 'teacher', 'homeroom_teacher'],
              width: 'wide',
            },
          },
          {
            path: 'papers', name: 'school.exams.papers',
            component: () => import('@/pages/school/exams/PapersListPage.vue'),
            meta: { requiredCapabilities: ['exams'],
              title: 'Exams — Papers',
              requiredRoles: ['school_admin', 'principal', 'deputy_principal',
                              'head_of_department', 'teacher', 'homeroom_teacher'],
              width: 'wide',
            },
          },
          {
            // BEFORE 'papers/:paperId', like 'marks': a param route declared
            // first swallows a literal sibling.
            path: 'rankings', name: 'school.exams.rankings',
            component: () => import('@/pages/school/exams/RankingsPage.vue'),
            meta: { requiredCapabilities: ['exams'],
              title: 'Exams — Rankings',
              requiredRoles: ['school_admin', 'principal', 'deputy_principal',
                              'head_of_department', 'teacher', 'homeroom_teacher'],
              // marks.view is what actually gates it; a teacher sees only the
              // classes they teach, enforced server-side.
              requiredPermissions: ['marks.view'],
              width: 'wide',
            },
          },
          {
            // BEFORE 'papers/:paperId', like 'rankings' and 'marks': a param
            // route declared first swallows a literal sibling.
            path: 'report-cards', name: 'school.exams.report-cards',
            component: () => import('@/pages/school/exams/ReportCardsPage.vue'),
            meta: { requiredCapabilities: ['exams'],
              title: 'Exams — Report cards',
              requiredRoles: ['school_admin', 'principal', 'deputy_principal',
                              'head_of_department', 'teacher', 'homeroom_teacher'],
              // marks.view gates the page; generate, publish and reopen each
              // need grading.manage, enforced server-side.
              requiredPermissions: ['marks.view'],
              width: 'wide',
            },
          },
          {
            // BEFORE 'papers/:paperId' for the same reason that one sits after
            // 'papers': a param route declared first swallows a literal sibling.
            path: 'marks', name: 'school.exams.marks',
            component: () => import('@/pages/school/exams/MarksPage.vue'),
            meta: { requiredCapabilities: ['exams'],
              title: 'Exams — Marks',
              requiredRoles: ['school_admin', 'principal', 'deputy_principal',
                              'head_of_department', 'teacher', 'homeroom_teacher'],
              // The role list admits a teacher; marks.view is what actually
              // gates reading a pupil's marks.
              requiredPermissions: ['marks.view'],
              width: 'wide',
            },
          },
          {
            // AFTER 'papers': a param route declared first would swallow it.
            path: 'papers/:paperId', name: 'school.exams.paper',
            component: () => import('@/pages/school/exams/PaperPage.vue'),
            meta: { requiredCapabilities: ['exams'],
              title: 'Exams — Paper',
              requiredRoles: ['school_admin', 'principal', 'deputy_principal',
                              'head_of_department', 'teacher', 'homeroom_teacher'],
              width: 'wide',
            },
          },
          {
            path: 'questions', name: 'school.exams.questions',
            component: () => import('@/pages/school/exams/QuestionBankPage.vue'),
            meta: { requiredCapabilities: ['exams'],
              title: 'Exams — Question bank',
              requiredRoles: ['school_admin', 'principal', 'deputy_principal',
                              'head_of_department', 'teacher', 'homeroom_teacher'],
              width: 'wide',
            },
          },
        ],
      },
      {
        path: 'grading/scales', name: 'school.grading.scales',
        component: () => import('@/pages/school/grading/GradingScalesPage.vue'),
        meta: { requiredCapabilities: ['grading'],
          title: 'Grading — Scales',
          requiredRoles: ['school_admin', 'principal', 'deputy_principal'],
          // The role list is the coarse gate; grading.manage is what actually
          // decides, and it is deliberately leadership-only — editing a band
          // restates every grade in the school.
          requiredPermissions: ['grading.manage'],
          width: 'wide',
        },
      },
      {
        path: 'grading/promotion', name: 'school.grading.promotion',
        component: () => import('@/pages/school/grading/PromotionPage.vue'),
        meta: { requiredCapabilities: ['grading'],
          title: 'Grading — Promotion',
          requiredRoles: ['school_admin', 'principal', 'deputy_principal'],
          requiredPermissions: ['grading.manage'],
          width: 'wide',
        },
      },
      {
        path: 'attendance',
        component: () => import('@/pages/school/Attendance.vue'),
        // teacher and homeroom_teacher are included because marking their own
        // register is the whole point of the feature. The parent guard runs
        // first, so leaving them out here would make it unreachable for exactly
        // the people who use it daily.
        meta: {
          title: 'Attendance',
          requiredRoles: ['school_admin', 'principal', 'deputy_principal',
                          'head_of_department', 'teacher', 'homeroom_teacher'],
          width: 'wide',
        },
        children: [
          { path: '', redirect: { name: 'school.attendance.register' } },
          {
            path: 'register', name: 'school.attendance.register',
            component: () => import('@/pages/school/attendance/RegisterPage.vue'),
            meta: { anyOfModules: ['ACADEMICS'], requiredCapabilities: ['attendance.register'],
              title: 'Attendance — Register',
              requiredRoles: ['school_admin', 'principal', 'deputy_principal',
                              'head_of_department', 'teacher', 'homeroom_teacher'],
              width: 'wide',
            },
          },
          {
            // Marking the staff register is leadership work: attendance.manage
            // is required by the API, and there is no per-department scoping
            // in v1.
            path: 'staff', name: 'school.attendance.staff',
            component: () => import('@/pages/school/attendance/StaffRegisterPage.vue'),
            meta: { requiredCapabilities: ['attendance.staff'],
              title: 'Attendance — Staff',
              requiredRoles: ['school_admin', 'principal', 'deputy_principal'],
              width: 'wide',
            },
          },
          {
            // head_of_department can read reports, but the Completeness tab
            // still hides itself without attendance.manage — the route guard
            // and the tab guard agree rather than one contradicting the other.
            path: 'reports', name: 'school.attendance.reports',
            component: () => import('@/pages/school/attendance/ReportsPage.vue'),
            meta: { requiredCapabilities: ['attendance.reports'],
              title: 'Attendance — Reports',
              requiredRoles: ['school_admin', 'principal', 'deputy_principal',
                              'head_of_department'],
              width: 'wide',
            },
          },
        ],
      },
      {
        path: 'timetable',
        component: () => import('@/pages/school/Timetable.vue'),
        // teacher and homeroom_teacher are included because PR11's views tab
        // is theirs to read. The parent guard runs first, so omitting them
        // here would make their own timetable unreachable.
        meta: {
          title: 'Timetable',
          requiredRoles: ['school_admin', 'principal', 'deputy_principal',
                          'head_of_department', 'teacher', 'homeroom_teacher'],
          width: 'wide',
        },
        children: [
          { path: '', redirect: { name: 'school.timetable.configuration' } },
          {
            path: 'configuration', name: 'school.timetable.configuration',
            component: () => import('@/pages/school/timetable/ConfigurationPage.vue'),
            meta: { requiredCapabilities: ['timetable'],
              title: 'Timetable — Configuration',
              requiredRoles: ['school_admin', 'principal', 'deputy_principal'],
              width: 'wide',
            },
          },
          {
            path: 'grid', name: 'school.timetable.grid',
            component: () => import('@/pages/school/timetable/GridEditorPage.vue'),
            // Editing is leadership-only; head_of_department is deliberately
            // absent. The backend does not scope timetable placement by
            // department, so a department head editing the school-wide grid
            // would be unbounded — unlike allocations, which SP2 does scope.
            meta: { requiredCapabilities: ['timetable'],
              title: 'Timetable — Grid',
              requiredRoles: ['school_admin', 'principal', 'deputy_principal'],
              width: 'wide',
            },
          },
          {
            // The widest guard in the section: a teacher reading their own
            // timetable is the most common use of this feature, and the tab
            // is read-only by construction.
            path: 'views', name: 'school.timetable.views',
            component: () => import('@/pages/school/timetable/ViewsPage.vue'),
            meta: { requiredCapabilities: ['timetable'],
              title: 'Timetable — Views',
              requiredRoles: ['school_admin', 'principal', 'deputy_principal',
                              'head_of_department', 'teacher', 'homeroom_teacher'],
              width: 'wide',
            },
          },
          {
            // head_of_department included: arranging cover is a day-to-day
            // departmental job, which is why V288 grants them
            // substitution.manage.
            path: 'cover', name: 'school.timetable.cover',
            component: () => import('@/pages/school/timetable/CoverPage.vue'),
            meta: { requiredCapabilities: ['timetable'],
              title: 'Timetable — Cover',
              requiredRoles: ['school_admin', 'principal', 'deputy_principal',
                              'head_of_department'],
              width: 'wide',
            },
          },
          {
            path: 'rooms', name: 'school.timetable.rooms',
            component: () => import('@/pages/school/timetable/RoomsPage.vue'),
            meta: { requiredCapabilities: ['timetable'],
              title: 'Timetable — Rooms',
              requiredRoles: ['school_admin', 'principal', 'deputy_principal'],
              width: 'wide',
            },
          },
        ],
      },
      {
        path: 'staffing',
        component: () => import('@/pages/school/Staffing.vue'),
        // Includes teacher and homeroom_teacher because the Leave child is
        // theirs to use. The parent guard runs first, so omitting them here
        // would make leave unreachable for exactly the people who request it.
        meta: {
          title: 'Staffing',
          requiredRoles: ['school_admin', 'principal', 'deputy_principal',
                          'head_of_department', 'teacher', 'homeroom_teacher'],
          width: 'wide',
        },
        children: [
          { path: '', redirect: { name: 'school.staffing.allocations' } },
          {
            path: 'allocations', name: 'school.staffing.allocations',
            component: () => import('@/pages/school/staffing/AllocationsPage.vue'),
            meta: { requiredCapabilities: ['staffing'],
              title: 'Staffing — Allocations',
              requiredRoles: ['school_admin', 'principal', 'deputy_principal',
                              'head_of_department'],
              width: 'wide',
            },
          },
          {
            // Any staff member may request leave, so this is the widest guard
            // in the section. The page itself scopes what is visible.
            path: 'leave', name: 'school.staffing.leave',
            component: () => import('@/pages/school/staffing/LeavePage.vue'),
            meta: { requiredCapabilities: ['staffing'],
              title: 'Staffing — Leave',
              requiredRoles: ['school_admin', 'principal', 'deputy_principal',
                              'head_of_department', 'teacher', 'homeroom_teacher'],
              width: 'wide',
            },
          },
          {
            path: 'entitlements', name: 'school.staffing.entitlements',
            component: () => import('@/pages/school/staffing/EntitlementsPage.vue'),
            meta: { requiredCapabilities: ['staffing'],
              title: 'Staffing — Entitlements',
              requiredRoles: ['school_admin', 'principal', 'deputy_principal'],
              width: 'wide',
            },
          },
          {
            // Not head_of_department: a class teacher spans every subject in a
            // stream, so it is not a departmental decision.
            path: 'class-teachers', name: 'school.staffing.classTeachers',
            component: () => import('@/pages/school/staffing/ClassTeachersPage.vue'),
            meta: { requiredCapabilities: ['staffing'],
              title: 'Staffing — Class teachers',
              requiredRoles: ['school_admin', 'principal', 'deputy_principal'],
              width: 'wide',
            },
          },
        ],
      },
      {
        path: 'curriculum',
        component: () => import('@/pages/school/Curriculum.vue'),
        meta: { title: 'Curriculum', requiredRoles: ['school_admin', 'principal', 'deputy_principal', 'head_of_department'], width: 'wide' },
        children: [
          { path: '', redirect: { name: 'school.curriculum.subjects' } },
          {
            path: 'subjects', name: 'school.curriculum.subjects',
            component: () => import('@/pages/school/curriculum/SubjectsPage.vue'),
            meta: { requiredCapabilities: ['curriculum'], title: 'Curriculum — Subjects', requiredRoles: ['school_admin', 'principal', 'deputy_principal', 'head_of_department'], width: 'wide' },
          },
          {
            path: 'subjects/:id', name: 'school.curriculum.subjectDetail',
            component: () => import('@/pages/school/curriculum/SubjectDetailPage.vue'),
            meta: { title: 'Curriculum — Subject', requiredRoles: ['school_admin', 'principal', 'deputy_principal', 'head_of_department'], width: 'wide' },
          },
          {
            path: 'departments', name: 'school.curriculum.departments',
            component: () => import('@/pages/school/curriculum/DepartmentsPage.vue'),
            meta: { requiredCapabilities: ['curriculum'],
              title: 'Curriculum — Departments',
              requiredRoles: ['school_admin', 'principal', 'deputy_principal',
                              'head_of_department'],
              width: 'wide',
            },
          },
          {
            path: 'adopt', name: 'school.curriculum.adopt',
            component: () => import('@/pages/school/curriculum/AdoptTemplatePage.vue'),
            meta: { requiredCapabilities: ['curriculum'],
              title: 'Curriculum — Adopt template',
              requiredRoles: ['school_admin', 'principal'],
              width: 'wide',
            },
          },
        ],
      },
      {
        path: 'transport',
        component: () => import('@/pages/school/Transport.vue'),
        meta: { title: 'Transport', requiredRoles: ['school_admin', 'transport_manager'], width: 'wide' },
        children: [
          { path: '', redirect: { name: 'school.transport.buses' } },
          {
            path: 'buses', name: 'school.transport.buses',
            component: () => import('@/pages/school/transport/BusesPage.vue'),
            meta: { requiredCapabilities: ['transport'], title: 'Transport — Buses', requiredRoles: ['school_admin', 'transport_manager'], width: 'wide' },
          },
          {
            path: 'routes', name: 'school.transport.routes',
            component: () => import('@/pages/school/transport/RoutesPage.vue'),
            meta: { requiredCapabilities: ['transport'], title: 'Transport — Routes', requiredRoles: ['school_admin', 'transport_manager'], width: 'wide' },
          },
          {
            path: 'assignments', name: 'school.transport.assignments',
            component: () => import('@/pages/school/transport/AssignmentsPage.vue'),
            meta: { requiredCapabilities: ['transport'], title: 'Transport — Assignments', requiredRoles: ['school_admin', 'transport_manager'], width: 'wide' },
          },
          // Classes, streams, the calendar and promotion live under Settings
          // only. These paths were briefly a second copy in the Transport menu;
          // they redirect so a saved link still lands somewhere useful.
          { path: 'classes', redirect: '/school/settings/classes' },
          { path: 'streams', redirect: '/school/settings/streams' },
          { path: 'calendar', redirect: '/school/settings/calendar' },
          { path: 'promotion', redirect: '/school/people/students/promotion' },
          {
            path: 'roster', name: 'school.transport.roster',
            component: () => import('@/pages/school/transport/RosterPage.vue'),
            meta: { requiredCapabilities: ['transport'], title: 'Transport — Roster', requiredRoles: ['school_admin', 'transport_manager'], width: 'wide' },
          },
          {
            path: 'tags', name: 'school.transport.tags',
            component: () => import('@/pages/school/transport/TagsPage.vue'),
            meta: { requiredCapabilities: ['transport'], title: 'Transport — Tags', requiredRoles: ['school_admin', 'transport_manager'], width: 'wide' },
          },
          {
            path: 'activity', name: 'school.transport.activity',
            component: () => import('@/pages/school/transport/ActivityPage.vue'),
            meta: { requiredCapabilities: ['transport'], title: 'Transport — Activity', requiredRoles: ['school_admin', 'transport_manager'], width: 'wide' },
          },
          {
            path: 'operators', name: 'school.transport.operators',
            component: () => import('@/pages/school/transport/OperatorsPage.vue'),
            meta: { requiredCapabilities: ['transport'], title: 'Transport — Operators', requiredRoles: ['school_admin', 'transport_manager'], width: 'wide' },
          },
          {
            path: 'analytics', name: 'school.transport.analytics',
            component: () => import('@/pages/school/transport/AnalyticsPage.vue'),
            meta: { requiredCapabilities: ['transport'], title: 'Transport — Analytics', requiredRoles: ['school_admin', 'transport_manager'], width: 'wide' },
          },
          {
            path: 'compliance', name: 'school.transport.compliance',
            component: () => import('@/pages/school/transport/CompliancePage.vue'),
            meta: { requiredCapabilities: ['transport'], title: 'Transport — Compliance', requiredRoles: ['school_admin', 'transport_manager'], width: 'wide' },
          },
          {
            path: 'runs', name: 'school.transport.runs',
            component: () => import('@/pages/school/transport/RunsPage.vue'),
            meta: { requiredCapabilities: ['transport'], title: 'Transport — Runs', requiredRoles: ['school_admin', 'transport_manager'], width: 'wide' },
          },
          {
            // sessionId comes from the URL: this view is opened from a run,
            // never browsed to on its own.
            path: 'runs/:sessionId', name: 'school.transport.run',
            component: () => import('@/pages/school/transport/LiveRunPage.vue'),
            props: true,
            meta: { requiredCapabilities: ['transport'], title: 'Transport — Live run', requiredRoles: ['school_admin', 'transport_manager'], width: 'wide' },
          },
        ],
      },
      {
        path: 'reports',
        component: () => import('@/pages/school/Reports.vue'),
        // CANTEEN_ADMIN gets to land on the Reports page so they can reach
        // the Canteen tab. The Finance / Operations / Saved tabs remain
        // school_admin-only via their own per-tab requiredRoles below.
        meta: { title: 'Reports', requiredRoles: ['school_admin', 'canteen_admin'], width: 'wide' },
        // The first tab this user's roles AND this school's modules allow.
        // A fixed Finance landing bounced a canteen admin, and a school with
        // invoicing switched off, off a page they could otherwise use.
        redirect: () => {
          const principal = principalOf(useAuthStore())
          const first = REPORT_TABS.find((t) =>
            canOpenPath(principal, router, `/school/reports/${t}`))
          return { name: `school.reports.${first ?? 'finance'}` }
        },
        children: [
          {
            path: 'finance',
            name: 'school.reports.finance',
            component: () => import('@/pages/school/reports/FinanceTab.vue'),
            meta: { anyOfModules: ['FINANCE'], requiredCapabilities: ['finance.billing'], title: 'Reports — Finance', requiredRoles: ['school_admin'], width: 'wide' },
          },
          {
            path: 'operations',
            name: 'school.reports.operations',
            component: () => import('@/pages/school/reports/OperationsTab.vue'),
            meta: { title: 'Reports — Operations', requiredRoles: ['school_admin'], width: 'wide' },
          },
          {
            path: 'canteen',
            name: 'school.reports.canteen',
            component: () => import('@/pages/school/reports/CanteenTab.vue'),
            meta: { requiredCapabilities: ['canteen'], title: 'Reports — Canteen', requiredRoles: ['school_admin', 'canteen_admin'], width: 'wide' },
          },
          {
            path: 'transactions',
            name: 'school.reports.transactions',
            component: () => import('@/pages/school/reports/TransactionsTab.vue'),
            // Both roles see the page; the page calls the same endpoint and
            // the backend pins canteenOnly=true for canteen admins.
            meta: { anyOfModules: ['FINANCE', 'CANTEEN'], requiredCapabilities: ['finance.billing'], title: 'Reports — Transactions', requiredRoles: ['school_admin', 'canteen_admin'], width: 'wide' },
          },
          {
            path: 'saved',
            name: 'school.reports.saved',
            component: () => import('@/pages/school/reports/SavedTab.vue'),
            meta: { title: 'Reports — Saved', requiredRoles: ['school_admin'], width: 'wide' },
          },
        ],
      },
      {
        path: 'settings',
        name: 'school.settings',
        component: () => import('@/pages/school/Settings.vue'),
        meta: { title: 'Settings', requiredRoles: ['school_admin'] },
      },
      {
        path: 'settings/classes',
        name: 'school.settings.classes',
        component: () => import('@/pages/school/settings/ClassesPage.vue'),
        meta: { title: 'Classes', requiredRoles: ['school_admin'] },
      },
      {
        path: 'settings/streams',
        name: 'school.settings.streams',
        component: () => import('@/pages/school/settings/StreamsPage.vue'),
        meta: { title: 'Streams', requiredRoles: ['school_admin'] },
      },
      {
        path: 'settings/profile',
        name: 'school.settings.profile',
        component: () => import('@/pages/school/settings/ProfilePage.vue'),
        meta: { title: 'School profile', requiredRoles: ['school_admin'] },
      },
      {
        path: 'settings/integrations',
        name: 'school.settings.integrations',
        component: () => import('@/pages/school/settings/IntegrationsPage.vue'),
        meta: { title: 'Integrations', requiredRoles: ['school_admin'] },
      },
      {
        path: 'settings/wallet',
        name: 'school.settings.wallet',
        component: () => import('@/pages/school/settings/WalletPage.vue'),
        meta: { anyOfModules: ['FINANCE', 'CANTEEN'], title: 'Wallet controls', requiredRoles: ['school_admin'] },
      },
      {
        path: 'settings/payment-methods',
        name: 'school.settings.paymentMethods',
        component: () => import('@/pages/school/settings/PaymentMethodsPage.vue'),
        meta: { anyOfModules: ['FINANCE', 'CANTEEN'], title: 'Payment methods', requiredRoles: ['school_admin'] },
      },
      {
        path: 'settings/transport-charges',
        name: 'school.settings.transport-charges',
        component: () => import('@/pages/school/settings/TransportChargesPage.vue'),
        meta: {
          title: 'Transport charges',
          requiredRoles: ['school_admin'],
          requiredCapabilities: ['transport'],
        },
      },
      {
        path: 'settings/canteen',
        name: 'school.settings.canteen',
        component: () => import('@/pages/school/settings/CanteenSettingsPage.vue'),
        meta: {
          title: 'Canteen settings',
          requiredRoles: ['school_admin'],
          requiredCapabilities: ['canteen'],
          requiredPermissions: ['canteen_settings.view'],
        },
      },
      {
        path: 'settings/calendar',
        name: 'school.settings.calendar',
        component: () => import('@/pages/school/settings/CalendarPage.vue'),
        meta: { title: 'Academic calendar', requiredRoles: ['school_admin'] },
      },
      {
        path: 'settings/invoice-template',
        name: 'school.settings.invoiceTemplate',
        component: () => import('@/pages/school/settings/InvoiceTemplatePage.vue'),
        meta: { anyOfModules: ['FINANCE'], requiredCapabilities: ['finance.billing'], title: 'Invoice template', requiredRoles: ['school_admin'] },
      },
      {
        path: 'settings/notifications',
        name: 'school.settings.notifications',
        component: () => import('@/pages/school/settings/NotificationsPage.vue'),
        meta: { title: 'Notifications', requiredRoles: ['school_admin'] },
      },
      {
        path: 'settings/audit',
        name: 'school.settings.audit',
        component: () => import('@/pages/school/settings/AuditPage.vue'),
        meta: { title: 'Audit log', requiredRoles: ['school_admin'], width: 'wide' },
      },
    ],
  },

  // --- Transport-only staff landing (Task 6) ----------------------------------
  // bus_operator / driver users carry no school_admin/transport_manager role,
  // so the full /school/* surface (SchoolLayout + its requiredRoles gates)
  // is closed to them. This is a standalone top-level route (no layout
  // wrapper) mirroring /pay/:token below — a minimal read-only landing, not
  // part of the School Admin Portal chrome.
  {
    path: '/transport/me',
    name: 'transport.me',
    component: () => import('@/pages/transport/MyAssignmentPage.vue'),
    meta: { title: 'My Transport Assignment', requiredRoles: ['bus_operator', 'driver'] },
  },

  // --- Public payment page ---------------------------------------------------
  // Anonymous parent-facing route, mounted OUTSIDE SchoolLayout / PlatformLayout
  // so the admin chrome / sidebar do not render. The router guard already
  // honours `meta.public` for unauthenticated access.
  {
    path: '/pay/:token',
    name: 'public.invoice',
    component: () => import('@/pages/public/InvoicePayPage.vue'),
    meta: { public: true, title: 'Pay invoice' },
  },

  // --- Fallback --------------------------------------------------------------
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    redirect: () => ({ path: '/auth/login' }),
  },
]

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(_to, _from, saved) {
    return saved ?? { top: 0 }
  },
})

/** The auth store as the pure access helpers want it. */
export function principalOf(auth: ReturnType<typeof useAuthStore>): Principal {
  return {
    roles: auth.user?.roles ?? [],
    permissions: auth.user?.permissions ?? [],
    isSuperAdmin: auth.isSuperAdmin,
    // Undefined, not [] — a pre-rollout session must not read as 'entitled
    // to nothing'. See `passesRecord` in access.ts.
    capabilities: auth.user?.capabilities,
    modules: auth.user?.modules,
  }
}

/**
 * Where to send a user who may not open where they were going.
 *
 * <p>Never back to the sign-in screen for somebody who is signed in. That was
 * the old fallthrough, and it made every authenticated non-admin staff member
 * look like their password had failed: `/school` admitted only school and
 * canteen admins, so a teacher or head of department bounced from the login
 * screen to the login screen with nothing said. Only a session with no
 * reachable surface at all ends up there now.
 */
function elsewhere(
  auth: ReturnType<typeof useAuthStore>,
  fromPath: string,
): { name: string } {
  if (auth.isSuperAdmin) return { name: 'platform.dashboard' }
  // Transport-only staff (bus_operator/driver) have no /school surface at all;
  // their one page is the minimal My Assignment landing.
  if (auth.isTransportOnly) return { name: 'transport.me' }

  const landing = landingRouteName(principalOf(auth), router)
  // Compare resolved paths, not a name against a path: redirecting to the very
  // route that just failed would spin the guard.
  if (landing && router.resolve({ name: landing }).path !== fromPath) {
    return { name: landing }
  }

  // Genuinely nothing to show them. Logging out is the honest outcome; leaving
  // them on a blank shell is not.
  return { name: 'auth.login' }
}

// Global navigation guard — reads auth state from the Pinia store.
// Hard-refresh flow: in-memory accessToken is gone, but the HttpOnly
// kori_refresh cookie survives. Try a silent /v1/auth/refresh before
// bouncing to login so the user stays on their current page.
router.beforeEach(async (to) => {
  const auth = useAuthStore()

  // Login intent forwarded to the origin root. Other apps (parent-web's auth
  // middleware) treat `/` as the "unified login" and redirect unauthenticated
  // users to `/?next=/parent/...`. The root is now the public landing page, so
  // forward an explicit, unauthenticated login intent to the real login screen
  // (which honours `?next=` and cross-app-jumps parents back to /parent/).
  if (
    to.path === '/' &&
    !auth.isAuthenticated &&
    typeof to.query.next === 'string' &&
    to.query.next.length > 0
  ) {
    return { name: 'auth.login', query: { next: to.query.next } }
  }

  // A parent with a warm (in-memory) session who opens the public marketing
  // root belongs in their own app, not here. Checked before the meta.public
  // early-return below. Cold-loaded parents (no in-memory session) are
  // intentionally NOT bounced — detecting them would require a silent refresh
  // on the public landing page, which we keep auth-free by design.
  if (
    to.path === '/' &&
    auth.isAuthenticated &&
    (auth.user?.roles ?? []).includes('parent')
  ) {
    window.location.assign('/parent/')
    return false
  }

  if (to.meta.public) return true

  if (!auth.isAuthenticated) {
    const token = await auth.refresh()
    if (!token) {
      return { name: 'auth.login', query: { next: to.fullPath } }
    }
  }

  // Parents land on admin-web when they hit the root URL (e.g. they
  // bookmarked the school's domain without `/parent/`). Bounce them to
  // the parent app immediately so they don't see admin chrome that
  // 403s every API call. window.location.assign because parent-web is
  // a separate Nuxt SPA at /parent/ — we're crossing app boundaries.
  const userRoles = auth.user?.roles ?? []
  if (userRoles.includes('parent')) {
    window.location.assign('/parent/')
    return false
  }

  const required = to.meta.requiredRoles
  if (required && required.length > 0) {
    const userRoles = auth.user?.roles ?? []
    const allowed = required.some((r) => userRoles.includes(r))
    if (!allowed) {
      // Impersonation transit: an active super-admin impersonation session
      // means the operator is intentionally entering a school surface they
      // wouldn't statically pass the role check for. The scoped access
      // token has school-level permissions baked into its claims (see
      // TenantController.startImpersonation: tenant.view + school.manage +
      // student.view + ...), so the server will accept their calls; we just
      // need to let the route resolve so the SchoolLayout mounts. The
      // ImpersonationBanner stays visible the whole time.
      if (auth.isSuperAdmin && auth.isImpersonating && to.path.startsWith('/school')) {
        return true
      }
      return elsewhere(auth, to.path)
    }
  }

  // Permission-based guard. Super Admins implicitly have every permission; for
  // other roles we consult the permissions set on the user object.
  const requiredPerms = to.meta.requiredPermissions
  if (requiredPerms && requiredPerms.length > 0 && !auth.isSuperAdmin) {
    const userPerms = new Set(auth.user?.permissions ?? [])
    const ok = requiredPerms.every((p) => userPerms.has(p))
    if (!ok) return elsewhere(auth, to.path)
  }

  return true
})

router.afterEach((to) => {
  const segment = to.meta.title ?? 'Admin'
  document.title = `${segment} · SYNC`
  // Navigation succeeded — clear any stale-chunk reload guard for this path so
  // a future redeploy can recover it again.
  try {
    sessionStorage.removeItem(`kori:chunk-reload:${to.fullPath}`)
  } catch {
    /* sessionStorage unavailable (private mode quirks) — non-fatal */
  }
})

// Stale-deploy recovery. After a redeploy, an already-open tab still runs the
// previous app whose lazy route chunks were renamed — and the atomic-swap
// deploy deleted the old files — so importing the next route's chunk 404s and
// the navigation silently aborts (the page just doesn't change). Vue Router
// surfaces that failure here; hard-reload to the target so the browser fetches
// the fresh index.html + current chunk hashes. A one-shot sessionStorage guard
// prevents a reload loop when the import genuinely can't be satisfied (offline,
// a chunk that's really missing).
router.onError((error, to) => {
  const message = String((error as Error)?.message ?? error)
  const isChunkLoadError =
    /dynamically imported module|Importing a module script failed|ChunkLoadError|error loading dynamically imported module/i.test(
      message,
    )
  if (!isChunkLoadError || !to?.fullPath) return
  const key = `kori:chunk-reload:${to.fullPath}`
  try {
    if (sessionStorage.getItem(key)) return // already attempted — avoid a loop
    sessionStorage.setItem(key, '1')
  } catch {
    /* sessionStorage unavailable — proceed with the reload anyway */
  }
  window.location.assign(to.fullPath)
})
